"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  translations,
  type Locale,
  type Translations,
} from "./translations";

const SUPPORTED_LOCALES: Locale[] = ["en", "fr", "es", "de", "pt"];

function detectBrowserLocale(): Locale {
  if (typeof navigator === "undefined") return "en";

  // Check navigator.languages first, then navigator.language
  const candidates = [
    ...(navigator.languages || []),
    navigator.language,
  ];

  for (const lang of candidates) {
    if (!lang) continue;
    const code = lang.toLowerCase().split("-")[0];
    if (SUPPORTED_LOCALES.includes(code as Locale)) {
      return code as Locale;
    }
  }

  return "en";
}

type I18nContextType = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translations;
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("en");
  const manualChoice = useRef(false);

  // Auto-detect on mount — only if user hasn't manually picked
  useEffect(() => {
    if (!manualChoice.current) {
      const detected = detectBrowserLocale();
      setLocaleState(detected);
      document.documentElement.lang = detected;
    }
  }, []);

  // Manual selection always wins
  const setLocale = useCallback((l: Locale) => {
    manualChoice.current = true;
    setLocaleState(l);
    document.documentElement.lang = l;
  }, []);

  const t = translations[locale];

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}
